/**
 * Billing Service
 * Handles Stripe billing operations
 */

import { supabase } from '../lib/supabase';

class BillingService {
  /**
   * Get billing information for a user
   */
  async getBillingInfo(userId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('stripe_customers')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      // Return default data if no billing info exists
      if (!data) {
        return {
          plan_name: 'free',
          plan_status: 'active',
          current_period_end: null,
          metadata: {}
        };
      }

      return data;
    } catch (error) {
      console.error('Error fetching billing info:', error);
      throw error;
    }
  }

  /**
   * Create Stripe checkout session
   */
  async createCheckoutSession(userId, planName) {
    try {
      // This would call a backend function to create a Stripe checkout session
      const response = await fetch('/api/billing/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          planName,
          successUrl: `${window.location.origin}/settings/billing?success=true`,
          cancelUrl: `${window.location.origin}/settings/billing?canceled=true`,
        }),
      });

      if (!response.ok) throw new Error('Failed to create checkout session');

      const { sessionId, url } = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = url;
      
      return { sessionId };
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Create Stripe portal session (for managing subscription)
   */
  async createPortalSession(userId) {
    try {
      const response = await fetch('/api/billing/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          returnUrl: `${window.location.origin}/settings/billing`,
        }),
      });

      if (!response.ok) throw new Error('Failed to create portal session');

      const { url } = await response.json();
      
      // Redirect to Stripe Customer Portal
      window.location.href = url;
      
      return { url };
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(userId) {
    try {
      const response = await fetch('/api/billing/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) throw new Error('Failed to cancel subscription');

      return await response.json();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  /**
   * Get plan details
   */
  getPlanDetails(planName) {
    const plans = {
      free: {
        name: 'Free',
        price: '$0',
        features: [
          '100 items per month',
          'Basic search',
          'Community support'
        ]
      },
      pro: {
        name: 'Pro',
        price: '$12',
        features: [
          'Unlimited items',
          'Advanced search',
          'AI-powered insights',
          'Priority support',
          'Custom focuses'
        ]
      },
      team: {
        name: 'Team',
        price: '$29',
        features: [
          'Everything in Pro',
          'Team collaboration',
          'Shared focuses',
          'Admin controls',
          'SSO support'
        ]
      },
      enterprise: {
        name: 'Enterprise',
        price: 'Custom',
        features: [
          'Everything in Team',
          'Custom integrations',
          'Dedicated support',
          'SLA guarantee',
          'On-premise option'
        ]
      }
    };

    return plans[planName] || plans.free;
  }
}

export default new BillingService();

